import React from 'react';
import type { Plant } from '../types';

// Category color map
const CATEGORY_COLORS: Record<string, string> = {
  'Grünpflanzen':              'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Beet- und Balkonpflanzen':  'bg-pink-50 text-pink-700 border-pink-200',
  'Topf- und Kübelpflanzen':   'bg-violet-50 text-violet-700 border-violet-200',
  'Gemüsekulturen':            'bg-orange-50 text-orange-700 border-orange-200',
  'Kräuterkulturen':           'bg-lime-50 text-lime-700 border-lime-200',
};

interface PlantCardProps {
  plant: Plant;
  onEdit: (plant: Plant) => void;
  onDelete: (id: string) => void;
}

export const PlantCard: React.FC<PlantCardProps> = ({ plant, onEdit, onDelete }) => {
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const catColor = CATEGORY_COLORS[plant.category] ?? 'bg-sage/10 text-sage border-sage/20';

  const handleDelete = () => {
    if (confirmDelete) {
      onDelete(plant.id);
    } else {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3000);
    }
  };

  return (
    <div className="panel group relative flex flex-col overflow-hidden animate-fadeIn card-lift">
      {/* Thumbnail */}
      <div className="relative aspect-[4/3] overflow-hidden flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #f5f0e4 0%, #ede8d8 100%)' }}>
        {plant.image_url ? (
          <img
            src={plant.image_url}
            alt={plant.german_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="flex flex-col items-center gap-2">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor" className="text-sage/30">
              <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 008 20C19 20 22 3 22 3c-1 2-8 3.5-13.5 7"/>
            </svg>
            <span className="text-xs text-creamDim/40 font-mono">Kein Bild</span>
          </div>
        )}

        {/* Category badge on image */}
        <div className="absolute top-2 left-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${catColor}`}>
            {plant.category}
          </span>
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-white/85 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2 backdrop-blur-[2px]">
          <button onClick={() => onEdit(plant)} className="btn-ghost text-xs py-1.5 shadow-sm">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Bearbeiten
          </button>
          <button onClick={handleDelete}
            className={`text-xs px-3 py-1.5 rounded-xl border font-medium transition-all duration-200 ${
              confirmDelete ? 'bg-red-600 border-red-600 text-white' : 'border-red-200 text-red-500 hover:bg-red-50'
            }`}>
            {confirmDelete ? 'Bestätigen?' : 'Löschen'}
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col gap-1 bg-white">
        <h3 className="plant-name text-cream font-semibold text-base leading-tight line-clamp-1">
          {plant.german_name}
        </h3>
        <p className="botanical text-sage text-sm line-clamp-1">{plant.botanical_name}</p>
        {plant.notes && (
          <p className="text-creamDim/60 text-xs mt-1 line-clamp-2 leading-relaxed">{plant.notes}</p>
        )}
      </div>
    </div>
  );
};
